import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, MinusCircle, Trophy, RotateCcw, Home } from "lucide-react";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";

function ScoreCard({ result }) {
  const passed = result.score_10 >= 5;
  return (
    <div className={`relative overflow-hidden rounded-3xl p-8 ${
      passed ? "bg-gradient-to-br from-gcgreen to-gcgreen-dark border border-gcgold/30" : "bg-ink-800 border border-white/10"
    }`}>
      <div className="absolute inset-0 bg-dot-grid opacity-20" />
      {passed && <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-gcgold/20 blur-3xl" />}
      <div className="relative">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] mb-3 font-mono text-gcgold">Tu nota</div>
        <div className="flex items-baseline gap-2">
          <span className="font-display text-7xl lg:text-8xl font-extrabold text-white tracking-tighter">{result.score_10}</span>
          <span className="text-2xl text-white/50">/ 10</span>
        </div>
        <div className={`mt-3 text-sm font-medium ${passed ? "text-gcgold-light" : "text-slate-400"}`}>
          {passed ? "¡Aprobado! Sigue así." : "Sigue practicando, cada test cuenta."}
        </div>
      </div>
    </div>
  );
}

function StatBlock({ icon: Icon, label, value, tone }) {
  const tones = {
    green: "bg-emerald-950/40 border-emerald-700/30 text-emerald-300",
    red: "bg-red-950/40 border-red-700/30 text-red-300",
    gray: "bg-ink-700 border-white/5 text-slate-400",
  };
  return (
    <div className={`border rounded-2xl p-4 ${tones[tone] || tones.gray}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] font-bold uppercase tracking-[0.22em] font-mono">{label}</span>
      </div>
      <div className="font-display text-3xl font-extrabold mt-1 tracking-tighter">{value}</div>
    </div>
  );
}

export default function Result() {
  const { resultId } = useParams();
  const { state } = useLocation();
  const [result, setResult] = useState(state?.result || null);

  useEffect(() => {
    if (!result) api.get(`/results/${resultId}`).then((r) => setResult(r.data)).catch(console.error);
  }, [resultId, result]);

  if (!result) {
    return (
      <div className="min-h-screen bg-ink">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center text-slate-500">Cargando resultado...</div>
        <Footer />
      </div>
    );
  }

  const accuracy = result.total ? Math.round((result.correct / result.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-ink relative">
      <SEO title="Resultado del test" path={`/results/${resultId}`} />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />

      <div className="relative">
        <Header />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-gcgold mb-3 font-mono">Resultado</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tighter leading-[0.95]" data-testid="result-title">
            Test finalizado
          </h1>
          <p className="mt-2 text-sm text-slate-500 font-mono">{result.mode} · {result.total} preguntas</p>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.2fr_2fr] gap-4">
            <ScoreCard result={result} />
            <div className="grid grid-cols-3 gap-3 self-start">
              <StatBlock icon={CheckCircle2} label="Aciertos" value={result.correct} tone="green" />
              <StatBlock icon={XCircle} label="Errores" value={result.wrong} tone="red" />
              <StatBlock icon={MinusCircle} label="Blanco" value={result.blank} tone="gray" />
              <div className="col-span-3 bg-ink-800 border border-white/5 rounded-2xl p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2 font-mono">Precisión</div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-display text-3xl font-extrabold text-gcgold tracking-tighter">{accuracy}%</span>
                  <span className="font-mono text-xs text-slate-500">{result.correct}/{result.total}</span>
                </div>
                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-gcgreen to-gcgold rounded-full transition-all duration-700" style={{ width: `${accuracy}%` }} />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Button asChild className="bg-gcgold hover:bg-gcgold-light text-ink font-bold rounded-xl" data-testid="result-new-test">
              <Link to="/tests"><RotateCcw className="h-4 w-4 mr-2" /> Otro test</Link>
            </Button>
            <Button asChild variant="outline" className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white rounded-xl">
              <Link to="/dashboard"><Home className="h-4 w-4 mr-2" /> Panel</Link>
            </Button>
            <Button asChild variant="outline" className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white rounded-xl">
              <Link to="/stats"><Trophy className="h-4 w-4 mr-2" /> Estadísticas</Link>
            </Button>
          </div>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-4 tracking-tight">Revisión de respuestas</h2>
            <div className="space-y-3" data-testid="review-list">
              {result.items.map((item, idx) => {
                const isBlank = item.selected_index === null || item.selected_index === undefined;
                const isCorrect = item.is_correct;
                return (
                  <motion.div
                    key={item.question_id}
                    initial={{ opacity: 0, y: 6 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: Math.min(idx * 0.02, 0.3) }}
                    className={`border rounded-2xl p-5 bg-ink-800 ${
                      isBlank ? "border-white/5" : isCorrect ? "border-emerald-700/40" : "border-red-700/40"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-xs font-mono font-bold flex-shrink-0 ${
                        isBlank ? "bg-white/5 text-slate-500"
                        : isCorrect ? "bg-emerald-900/40 text-emerald-300"
                        : "bg-red-900/40 text-red-300"
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-white">{item.question}</div>
                        <div className="mt-3 space-y-1.5">
                          {item.options.map((opt, oIdx) => {
                            const isCorrectAnswer = oIdx === item.correct_index;
                            const isSelected = oIdx === item.selected_index;
                            return (
                              <div
                                key={oIdx}
                                className={`flex items-center gap-2.5 p-2.5 rounded-lg text-sm border ${
                                  isCorrectAnswer
                                    ? "border-emerald-700/40 bg-emerald-950/40 text-emerald-200"
                                    : isSelected
                                      ? "border-red-700/40 bg-red-950/40 text-red-200"
                                      : "border-transparent text-slate-400"
                                }`}
                              >
                                <span className="font-mono text-xs font-bold w-5">{String.fromCharCode(65 + oIdx)}</span>
                                <span className="flex-1">{opt}</span>
                                {isCorrectAnswer && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
                                {isSelected && !isCorrectAnswer && <XCircle className="h-4 w-4 text-red-400" />}
                              </div>
                            );
                          })}
                        </div>
                        {item.explanation && (
                          <div className="mt-4 border-l-2 border-gcgold pl-4 py-2 bg-gcgold/5 rounded-r-lg">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold mb-1 font-mono">Explicación</div>
                            <div className="text-sm text-slate-300 leading-relaxed">{item.explanation}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </div>
  );
}
